import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { Theme2care4Preset } from '../assets/2care4-preset';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { allAuthEffects } from './core/auth/auth.effects';
import { authenticationFeature } from './core/auth/auth.reducer';
import { LoaderInterceptor } from './core/layout/loader/loader.interceptor';
import { InsightsService } from './core/telemetry/insights.service';
import { ToastInterceptor } from './core/toasts/toast.interceptor';
import { priceAndAvailabilityEffects } from './features/markets/price-and-availability/price-and-availability.effects';
import { priceAndAvailabilityFeature } from './features/markets/price-and-availability/price-and-availability.reducer';
import { purchasePlannerEffects } from './features/markets/purchase-planner/purchase-planner.effects';
import { purchasePlannerFeature } from './features/markets/purchase-planner/purchase-planner.reducer';
import { templateEffects } from './features/markets/notes/features/templates/template.effects';
import { templateFeature } from './features/markets/notes/features/templates/template.reducer';
import { DataIntegrityErrorResponseHandler } from './shared/utils/http-error-responses/data-integrity-error-response-handler';
import { DefaultErrorResponseHandler } from './shared/utils/http-error-responses/default-error-response-handler';
import { ERROR_HANDLERS } from './shared/utils/http-error-responses/error-response-handler';
import { ValidationErrorResponseHandler } from './shared/utils/http-error-responses/validation-error-response-handler';
import { productsListEffects } from './features/markets/products/products.effects';
import { productsListFeature } from './features/markets/products/products.reducer';
import { productOverviewFeature } from './features/markets/products/pages/product-overview/product-overview.reducer';
import { productOverviewEffects } from './features/markets/products/pages/product-overview/product-overview.effects';
import { drugsListEffects } from './features/markets/drugs/drugs.effects';
import { drugsListFeature } from './features/markets/drugs/drugs.reducer';
import { linkingEffects } from './features/markets/linking/linking.effects';
import { linkingFeature } from './features/markets/linking/linking.reducer';
import { categoryEffects, categoryFeature } from './features/markets/notes/features/categories';
import { noteFeature, noteEffects } from './features/markets/notes';
import { userFeature } from './core/users/user.reducer';
import { userEffects } from './core/users/user.effects';

/**
 * Here we pass the configuration parameters to create an MSAL instance
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.azureAd.clientId,
      authority: `https://login.microsoftonline.com/${environment.azureAd.tenantId}`,
      knownAuthorities: [`login.microsoftonline.com`],
      redirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
    },
  });
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap: Map<string, string[]> = new Map<string, string[]>();
  // have this set if more microservice used or requires different scope for different controllers
  protectedResourceMap.set(
    environment.apiUrl + '/api', // This is for all controllers
    environment.azureAd.scopeUrls
  );
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.azureAd.scopeUrls],
    },
    loginFailedRoute: '/login-failed',
  };
}

/**
 * Error handler factory that uses Application Insights in test and production
 * and Angular's default error handler in development.
 */
export function errorHandlerFactory(): ErrorHandler {
  if (environment.environmentName !== 'development') {
    return new ApplicationinsightsAngularpluginErrorService();
  }
  // Use Angular's default error handler for development
  return new ErrorHandler();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ToastInterceptor,
      multi: true,
    },
    provideRouter(routes),
    importProvidersFrom(BrowserModule),
    provideClientHydration(),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: Theme2care4Preset,
    }),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MessageService,
    ConfirmationService,
    ValidationErrorResponseHandler,
    DataIntegrityErrorResponseHandler,
    DefaultErrorResponseHandler,
    { provide: ERROR_HANDLERS, useExisting: ValidationErrorResponseHandler, multi: true },
    { provide: ERROR_HANDLERS, useExisting: DataIntegrityErrorResponseHandler, multi: true },
    { provide: ERROR_HANDLERS, useExisting: DefaultErrorResponseHandler, multi: true },
    InsightsService,
    { provide: ErrorHandler, useFactory: errorHandlerFactory },
    provideState(authenticationFeature),
    provideState(priceAndAvailabilityFeature),
    provideState(productsListFeature),
    provideState(productOverviewFeature),
    provideState(drugsListFeature),
    provideState(linkingFeature),
    provideState(purchasePlannerFeature),
    provideState(categoryFeature),
    provideState(templateFeature),
    provideState(noteFeature),
    provideState(userFeature),
    provideStore(),
    provideEffects({
      ...allAuthEffects,
      ...priceAndAvailabilityEffects,
      ...productsListEffects,
      ...productOverviewEffects,
      ...drugsListEffects,
      ...linkingEffects,
      ...purchasePlannerEffects,
      ...categoryEffects,
      ...templateEffects,
      ...noteEffects,
      ...userEffects,
    }),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: environment.production,
      features: { dispatch: true, jump: true, persist: true, test: true },
    }),
  ],
};
